/**
 * 📝 DailyQuizEditor.js - Add/Edit Daily Quiz Question
 * * Form for creating and editing daily quiz questions:
 * - Question text with character count
 * - Species/Category selection
 * - Answer options (2-6 options)
 * - Correct answer selection
 * - Explanation text
 * - Image URLs (question and explanation)
 * - Difficulty selection
 * - Reference/source citation
 */

import React, { useState, useEffect } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase-config'; // ✅ FIXED PATH
import './DailyQuizEditor.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const SPECIES_OPTIONS = [
  { value: 'manatee', label: '🦭 Manatee', project: 'manatee' },
  { value: 'dolphin', label: '🐬 Dolphin', project: 'dolphin' },
  { value: 'humpback', label: '🐋 Humpback Whale', project: 'whale' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: '🟢 Easy' },
  { value: 'medium', label: '🟡 Medium' },
  { value: 'hard', label: '🔴 Hard' },
];

const EMPTY_OPTION = { id: '', text: '', isCorrect: false };

// ============================================================================
// COMPONENT
// ============================================================================

const DailyQuizEditor = ({ question, categories, onClose, onSave }) => {
  // Form state
  const [species, setSpecies] = useState(question?.species || 'manatee');
  const [categoryId, setCategoryId] = useState(question?.categoryId || '');
  const [questionText, setQuestionText] = useState(question?.question || '');
  const [questionType, setQuestionType] = useState(question?.questionType || 'multiple_choice');
  const [imageUrl, setImageUrl] = useState(question?.imageUrl || '');
  const [options, setOptions] = useState(
    question?.options || [
      { id: 'a', text: '', isCorrect: false },
      { id: 'b', text: '', isCorrect: false },
      { id: 'c', text: '', isCorrect: false },
      { id: 'd', text: '', isCorrect: false },
    ]
  );
  const [explanation, setExplanation] = useState(question?.explanation || '');
  const [explanationImageUrl, setExplanationImageUrl] = useState(question?.explanationImageUrl || '');
  const [difficulty, setDifficulty] = useState(question?.difficulty || 'medium');
  const [reference, setReference] = useState(question?.reference || '');
  const [referenceUrl, setReferenceUrl] = useState(question?.referenceUrl || '');
  const [isActive, setIsActive] = useState(question?.isActive !== false);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Filter categories by species
  const filteredCategories = categories.filter((c) => c.species === species);

  // Handle True/False type change
  useEffect(() => {
    if (questionType === 'true_false') {
      setOptions([
        { id: 'a', text: 'True', isCorrect: false },
        { id: 'b', text: 'False', isCorrect: false },
      ]);
    } else if (questionType === 'multiple_choice' && options.length === 2) {
      // Reset to 4 options when switching back to MC
      if (options[0]?.text === 'True' && options[1]?.text === 'False') {
        setOptions([
          { id: 'a', text: '', isCorrect: false },
          { id: 'b', text: '', isCorrect: false },
          { id: 'c', text: '', isCorrect: false },
          { id: 'd', text: '', isCorrect: false },
        ]);
      }
    }
  }, [questionType]);

  // Reset category when species changes
  useEffect(() => {
    if (!filteredCategories.find((c) => c.id === categoryId)) {
      setCategoryId(filteredCategories[0]?.id || '');
    }
  }, [species]);

  // =========================================================================
  // OPTION HANDLERS
  // =========================================================================

  const handleOptionTextChange = (index, text) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, text } : opt))
    );
  };

  const handleOptionCorrectChange = (index) => {
    setOptions((prev) =>
      prev.map((opt, i) => ({ ...opt, isCorrect: i === index }))
    );
  };

  const addOption = () => {
    if (options.length >= 6) return;
    const letters = 'abcdefgh';
    const nextId = letters[options.length];
    setOptions([...options, { id: nextId, text: '', isCorrect: false }]);
  };

  const removeOption = (index) => {
    if (options.length <= 2) return;
    const newOptions = options.filter((_, i) => i !== index);
    // Re-assign IDs
    const letters = 'abcdefgh';
    setOptions(newOptions.map((opt, i) => ({ ...opt, id: letters[i] })));
  };

  // =========================================================================
  // VALIDATION
  // =========================================================================

  const validate = () => {
    const newErrors = {};

    if (!questionText.trim()) {
      newErrors.question = 'Question text is required';
    }

    if (!categoryId) {
      newErrors.category = 'Please select a category';
    }

    const hasCorrectAnswer = options.some((o) => o.isCorrect);
    if (!hasCorrectAnswer) {
      newErrors.options = 'Please select a correct answer';
    }

    const hasEmptyOptions = options.some((o) => !o.text.trim());
    if (hasEmptyOptions && questionType === 'multiple_choice') {
      newErrors.options = 'All options must have text';
    }

    if (!explanation.trim()) {
      newErrors.explanation = 'Explanation is required';
    }

    if (imageUrl && !isValidUrl(imageUrl)) {
      newErrors.imageUrl = 'Invalid URL format';
    }

    if (explanationImageUrl && !isValidUrl(explanationImageUrl)) {
      newErrors.explanationImageUrl = 'Invalid URL format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // =========================================================================
  // SAVE
  // =========================================================================

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);

    try {
      const category = filteredCategories.find((c) => c.id === categoryId);
      const correctOption = options.find((o) => o.isCorrect);
      const speciesOpt = SPECIES_OPTIONS.find((s) => s.value === species);

      const data = {
        species,
        project: speciesOpt?.project || species,
        categoryId,
        category: category?.name || categoryId,
        question: questionText.trim(),
        questionType,
        imageUrl: imageUrl.trim() || null,
        options: options.filter((o) => o.text.trim()),
        correctAnswerId: correctOption?.id || 'a',
        explanation: explanation.trim(),
        explanationImageUrl: explanationImageUrl.trim() || null,
        difficulty,
        reference: reference.trim() || null,
        referenceUrl: referenceUrl.trim() || null,
        isActive,
        updatedAt: serverTimestamp(),
      };

      if (question?.id) {
        // Update existing
        await updateDoc(doc(db, 'dailyQuizQuestions', question.id), data);
      } else {
        // Create new
        data.createdAt = serverTimestamp();
        data.createdBy = 'admin';
        data.timesShown = 0;
        data.timesCorrect = 0;
        data.averageAccuracy = 0;
        await addDoc(collection(db, 'dailyQuizQuestions'), data);
      }

      onSave();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="dqe-container">
      {/* Header */}
      <div className="dqe-header">
        <button className="dqe-close" onClick={onClose}>
          ← Back
        </button>
        <h2>{question ? '✏️ Edit Question' : '➕ Add New Question'}</h2>
      </div>

      <div className="dqe-content">
        {/* Species & Category Row */}
        <div className="dqe-row">
          <div className="dqe-field">
            <label>Species *</label>
            <select value={species} onChange={(e) => setSpecies(e.target.value)}>
              {SPECIES_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="dqe-hint">
              → Goes to {SPECIES_OPTIONS.find(s => s.value === species)?.project} Chat
            </span>
          </div>

          <div className="dqe-field">
            <label>Category *</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className={errors.category ? 'error' : ''}
            >
              <option value="">Select category...</option>
              {filteredCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {errors.category && <span className="dqe-error">{errors.category}</span>}
          </div>

          <div className="dqe-field">
            <label>Difficulty</label>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
              {DIFFICULTY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Question Type */}
        <div className="dqe-field">
          <label>Question Type</label>
          <div className="dqe-type-toggle">
            <button
              type="button"
              className={questionType === 'multiple_choice' ? 'active' : ''}
              onClick={() => setQuestionType('multiple_choice')}
            >
              Multiple Choice
            </button>
            <button
              type="button"
              className={questionType === 'true_false' ? 'active' : ''}
              onClick={() => setQuestionType('true_false')}
            >
              True / False
            </button>
          </div>
        </div>

        {/* Question Text */}
        <div className="dqe-field">
          <label>
            Question *
            <span className="dqe-char-count">{questionText.length} characters</span>
          </label>
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter your question..."
            rows={3}
            className={errors.question ? 'error' : ''}
          />
          {errors.question && <span className="dqe-error">{errors.question}</span>}
        </div>

        {/* Question Image */}
        <div className="dqe-field">
          <label>Question Image URL (optional)</label>
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://s3.amazonaws.com/cetalingua/..."
            className={errors.imageUrl ? 'error' : ''}
          />
          {errors.imageUrl && <span className="dqe-error">{errors.imageUrl}</span>}
          {imageUrl && !errors.imageUrl && (
            <div className="dqe-image-preview">
              <img src={imageUrl} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
            </div>
          )}
        </div>

        {/* Answer Options */}
        <div className="dqe-field">
          <label>
            Answer Options *
            <span className="dqe-hint-inline">(Select the correct one)</span>
          </label>
          {errors.options && <span className="dqe-error">{errors.options}</span>}

          <div className="dqe-options">
            {options.map((option, index) => (
              <div key={option.id} className="dqe-option-row">
                <input
                  type="radio"
                  name="correctAnswer"
                  checked={option.isCorrect}
                  onChange={() => handleOptionCorrectChange(index)}
                  title="Mark as correct"
                />
                <span className="dqe-option-letter">{option.id.toUpperCase()}</span>
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(index, e.target.value)}
                  placeholder={`Option ${option.id.toUpperCase()}`}
                  disabled={questionType === 'true_false'}
                  className={option.isCorrect ? 'correct' : ''}
                />
                {questionType === 'multiple_choice' && options.length > 2 && (
                  <button
                    type="button"
                    className="dqe-remove-option"
                    onClick={() => removeOption(index)}
                    title="Remove option"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {questionType === 'multiple_choice' && options.length < 6 && (
              <button type="button" className="dqe-add-option" onClick={addOption}>
                + Add Option
              </button>
            )}
          </div>
        </div>

        {/* Explanation */}
        <div className="dqe-field">
          <label>
            Explanation *
            <span className="dqe-hint-inline">(Shown after answering)</span>
          </label>
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Explain why this is the correct answer..."
            rows={4}
            className={errors.explanation ? 'error' : ''}
          />
          {errors.explanation && <span className="dqe-error">{errors.explanation}</span>}
        </div>

        {/* Explanation Image */}
        <div className="dqe-field">
          <label>Explanation Image URL (optional)</label>
          <input
            type="text"
            value={explanationImageUrl}
            onChange={(e) => setExplanationImageUrl(e.target.value)}
            placeholder="https://..."
            className={errors.explanationImageUrl ? 'error' : ''}
          />
          {errors.explanationImageUrl && (
            <span className="dqe-error">{errors.explanationImageUrl}</span>
          )}
        </div>

        {/* Reference */}
        <div className="dqe-row">
          <div className="dqe-field">
            <label>Reference / Source (optional)</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Study name, author, year"
            />
          </div>
          <div className="dqe-field">
            <label>Reference URL (optional)</label>
            <input
              type="text"
              value={referenceUrl}
              onChange={(e) => setReferenceUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>

        {/* Active Toggle */}
        <div className="dqe-field">
          <label className="dqe-checkbox">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Active</span>
            <span className="dqe-hint-inline">
              (Inactive questions won't be shown to users)
            </span>
          </label>
        </div>
      </div>

     

      {/* Footer */}
      <div className="dqe-footer">
        <button className="dqe-btn dqe-btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="dqe-btn dqe-btn-success"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Saving...' : '💾 Save Question'}
        </button>
      </div>
    </div>
  );
};

export default DailyQuizEditor;
